import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Monitor, Fingerprint, MonitorSmartphone, Cpu, RefreshCw, Power, AlertTriangle } from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { StatusBadge } from "@/components/bio/StatusBadge";
import { EmptyState } from "@/components/bio/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const TYPE_ICON = { pos: Monitor, biometric: Fingerprint, kiosk: MonitorSmartphone };
const TYPE_LABEL = { pos: "Punto de venta", biometric: "Lector biométrico", kiosk: "Kiosco" };

export default function DevicesView() {
    const { user } = useAuth();
    const canEdit = user?.role === "super_admin" || user?.role === "school_admin";
    const canDisp = canEdit || user?.role === "cafeteria_operator";
    const [devices, setDevices] = useState(null);
    const [disps, setDisps] = useState(null);

    const load = useCallback(() => {
        api.get("/devices").then(({ data }) => setDevices(data));
        api.get("/dispensers").then(({ data }) => setDisps(data));
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggleDevice = async (d) => {
        const next = d.status === "online" ? "offline" : "online";
        const { data } = await api.patch(`/devices/${d.id}`, { status: next });
        setDevices((xs) => xs.map((x) => (x.id === d.id ? data : x)));
    };
    const refill = async (d) => {
        const { data } = await api.patch(`/dispensers/${d.id}`, { level: 100, status: "online" });
        setDisps((xs) => xs.map((x) => (x.id === d.id ? { ...data, cafeteria_name: x.cafeteria_name, alert: false } : x)));
        toast.success(`${d.name} reabastecida al 100%`);
    };

    return (
        <div className="space-y-5">
            <div>
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Dispositivos y dispensadoras</h1>
                <p className="text-bio-text-secondary mt-1 text-sm">Monitorea el estado de los equipos y las dispensadoras conectadas.</p>
            </div>

            <Tabs defaultValue="devices">
                <TabsList className="bg-[#EEF2F8] rounded-[12px] p-1">
                    <TabsTrigger value="devices" data-testid="tab-devices" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-bio-primary font-semibold">Dispositivos</TabsTrigger>
                    <TabsTrigger value="dispensers" data-testid="tab-dispensers" className="rounded-[8px] data-[state=active]:bg-white data-[state=active]:text-bio-primary font-semibold">Dispensadoras</TabsTrigger>
                </TabsList>

                <TabsContent value="devices" className="pt-4">
                    {!devices ? <Skeleton className="h-48 rounded-bio-card" /> : devices.length === 0 ? (
                        <div className="bg-white rounded-bio-card border border-bio-border-subtle"><EmptyState icon={Cpu} title="Sin dispositivos" /></div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {devices.map((d) => {
                                const Icon = TYPE_ICON[d.type] || Cpu;
                                return (
                                    <div key={d.id} data-testid={`device-${d.id}`} className="bg-white rounded-bio-card border border-bio-border-subtle p-4 shadow-bio-sm">
                                        <div className="flex items-start justify-between">
                                            <div className="h-11 w-11 rounded-bio bg-[#EEF7FC] text-bio-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                                            <StatusBadge status={d.status} />
                                        </div>
                                        <p className="font-bold text-sm mt-3">{d.name}</p>
                                        <p className="text-xs text-bio-text-muted">{TYPE_LABEL[d.type]} · {d.cafeteria_name}</p>
                                        {canEdit && (
                                            <BioButton variant="secondary" size="sm" className="w-full mt-3" data-testid={`device-toggle-${d.id}`} onClick={() => toggleDevice(d)}>
                                                <Power className="h-4 w-4" /> {d.status === "online" ? "Desactivar" : "Activar"}
                                            </BioButton>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="dispensers" className="pt-4">
                    {!disps ? <Skeleton className="h-48 rounded-bio-card" /> : disps.length === 0 ? (
                        <div className="bg-white rounded-bio-card border border-bio-border-subtle"><EmptyState icon={Cpu} title="Sin dispensadoras" /></div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {disps.map((d) => (
                                <div key={d.id} data-testid={`dispenser-${d.id}`} className={`bg-white rounded-bio-card border p-4 shadow-bio-sm ${(d.level < 40 || d.status === "offline") ? "border-bio-warning/50" : "border-bio-border-subtle"}`}>
                                    <div className="flex items-start justify-between">
                                        <p className="font-bold text-sm">{d.name}</p>
                                        <StatusBadge status={d.status} />
                                    </div>
                                    <p className="text-xs text-bio-text-muted">{d.cafeteria_name}</p>
                                    {(d.level < 40 || d.status === "offline") && <p className="mt-2 text-xs text-bio-error flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Requiere atención</p>}
                                    <div className="mt-3 h-2.5 rounded-full bg-bio-bg-alt overflow-hidden">
                                        <div className={`h-full rounded-full ${d.level < 15 ? "bg-bio-error" : d.level < 40 ? "bg-bio-warning" : "bg-bio-success"}`} style={{ width: `${d.level}%` }} />
                                    </div>
                                    <p className="text-xs text-bio-text-muted mt-1">{d.level}% de nivel</p>
                                    {canDisp && (
                                        <BioButton variant="secondary" size="sm" className="w-full mt-3" data-testid={`dispenser-refill-${d.id}`} onClick={() => refill(d)}>
                                            <RefreshCw className="h-4 w-4" /> Reabastecer
                                        </BioButton>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
